// DeleteAccountDialog.tsx — confirmation modal to permanently delete the active account.
//
// Deleting an account can never be undone, so it asks for the current password, exactly
// like changing the password or turning two-factor off. On success: toast, close,
// navigate to /signin. On failure the account is untouched and the dialog says why.

import { useState } from 'react';
import type { FormEvent } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Dialog, Button, TextField, useToast } from '@/ui';
import { useSession } from '@/hooks';
import { ApiError } from '@/core/lib/apiClient';

// ─── Styled components ─────────────────────────────────────────────────────────

const Stack = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Warn = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

// ─── Component ─────────────────────────────────────────────────────────────────

export function DeleteAccountDialog({ onClose }: { onClose: () => void }) {
  const { deleteAccount, isPremium } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function handleDelete(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (!password) {
      setError('Enter your password to delete your account.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await deleteAccount(password);
      toast('Your account has been deleted');
      onClose();
      void navigate('/signin');
    } catch (err) {
      const code = err instanceof ApiError ? err.code : '';
      setError(
        code === 'wrong_password'
          ? 'That password is not right. Your account has not been deleted.'
          : code === 'billing_cancel_failed'
            ? 'We could not cancel your Premium subscription, so your account has not been deleted. Try again in a moment.'
            : 'Something went wrong. Your account has not been deleted.',
      );
      setBusy(false);
    }
  }

  return (
    <Dialog open title="Delete account" onClose={onClose}>
      <Stack onSubmit={(e) => void handleDelete(e)} noValidate>
        <Warn>
          This permanently deletes your account and signs you out. It cannot be undone.
          {isPremium &&
            ' Your Premium subscription is cancelled first, so you are not charged again.'}
        </Warn>
        <TextField
          label="Your password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.currentTarget.value)}
          error={error || undefined}
        />
        <Row>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="danger" disabled={busy}>
            {busy ? 'Deleting' : 'Delete my account'}
          </Button>
        </Row>
      </Stack>
    </Dialog>
  );
}
